// onboarding.js — THE NEW-ACCOUNT WELCOME (Nika 2026-10-18: "make nicer user
// experience when new accounts join - help them personalize home page, switch
// language etc. … allow onboarding assistant, tour and skipping"; then "it
// should be wizard like onboarding and click based onboarding could be
// possible … avoid showing complex ui at first load").
//
// ONE moment, once per person. A NEW account (created within the last 14
// days) that has made no onboarding decision yet meets a full-screen setup
// surface on its home (components/OnboardingWizard.js: OnboardingGate →
// OnboardingWizard) — before the busy home: while its record is read the
// surface already holds the screen (onboardingHoldNeeded). From the greeting:
//
//   • THE WIZARD (Get started) — a few click steps, each a real choice:
//     the apps the home shows, the personal look,
//     the home's summary and App strip (functions/onboardingWizard.js).
//   • THE ASSISTANT — the same choices as a short conversation on the same
//     surface (onboardingChat*). A typed question hands off to the real
//     assistant (homeAiSend, the composer's own path).
//   • THE TOUR — six stops over the real chrome (functions/onboardingTour.js).
//   • SKIP — "Skip setup" at every step, each question's own skip, the tour's
//     "Skip tour", Escape. A skip is final: the welcome never comes back by
//     itself. The Personalise panel's "Getting started" group replays the
//     wizard, the assistant and the tour on demand.
//
// Every choice applies AT ONCE through the writers the Personalise panel
// already uses (homeLookSet / homeLookSetTheme / the board's own hide), so
// what the person picks is what the home shows when the surface leaves.
//
// PERSONAL SETUP is separate from workspace creation and administration.
// Fly owns signup, the Blueprint and provisioning. This flow uses the existing
// workspace and its language, and changes only this person's preferences.
// Workspace-wide language changes belong in workspace settings.
//
// THE DECISION is one bag on the user record, `user_preferences.onboarding`:
//   { v: 1, status: 'done' | 'skipped', via: 'wizard' | 'assistant' | 'tour' |
//     'skip' | 'ask' | 'fly' | 'pob', at: ISO, origin?: 'fly' | 'pob' }
//     (records of the first release may also say 'personalize' — any record
//     but a pending one, Fly's or a personalized sign-up's, is a decision;
//     `origin: 'fly'` marks a Fly sign-up whose decision was not `via: 'fly'`,
//     the tour's; `origin: 'pob'` the same for a personalized sign-up)
// Written the way homeLook writes (the home-prefs durability law): this
// session's copy first (`root._onboardingDecision`), then this device's copy
// (localStorage `symbols_onboarding:<userId>`), then the record on the
// SINGLE-FLIGHT queue every user_preferences writer shares
// (`root.__navbarSaveQueue`), the payload composed at SEND time from the live
// row (the update replaces the row). The first decision of a session wins; a
// replay never writes.
//
// A FLY ARRIVAL (Nika 2026-10-25, "Short ready screen (Recommended)"): a person
// who signed up through Fly has answered the workspace's questions already —
// industry, idea, company — so the full wizard never opens for them. Fly
// leaves `{ v: 1, status: 'pending', via: 'fly', at }` on the record instead,
// and the first load of the workspace shows ONE "Your workspace is ready"
// screen (functions/onboardingReady.js, components/OnboardingReadyStep.js):
// what Fly made, the tour offer and a plain close. `done` is written only
// after that screen really showed — `via: 'fly'` on close, `via: 'tour'` when
// the tour starts — and Fly's checklist reads `status === 'done'` only. Every
// other record, Fly's `done` of the first release included, stays a decision
// with no screen. The screen opens on the home AND on the app builder
// (`/add-app?prompt=…&fly=`), over the build thread: the build keeps running
// behind it and closing the screen shows the thread.
//
// A PERSONALIZED SIGN-UP (POB, PLAN §7b-2): a person who signed up with their
// website read never meets the wizard either. The SERVER writes `{ v: 1,
// status: 'pending', via: 'pob', at }` at sign-up, write-once (the client
// never writes it), and the first load of the workspace opens the BUILD BOARD
// on the same surface (functions/onboardingBuild.js,
// components/OnboardingBuildStep.js): one card per server step. `done, via
// 'pob'` is written when the board closes after every step has an outcome;
// a workspace with no board (404) shows nothing and the record waits. THE
// SIGN-UP RACE (POB lead 2026-10-30): a Fly write can win that write-once
// record. A pending Fly record on the HOME therefore asks the board first
// when this SDK can read it: a board (200) opens the board; none, or no
// answer in time, opens Fly's ready screen as before. The app builder, and an
// SDK without the read, open the ready screen at once.
//
// FRANK: every export is self-contained (FA204) — the vocabularies, the
// windows and the keys are restated where they are used; siblings are reached
// through `this.call`.

// Bare shared-library import, read only at CALL time (never at module scope) —
// the same deferred read pages/admin/workspace-settings.js makes for the
// language registry (a module-scope read of a declared library value is a
// stub phantom under frank).
import _wsShared from '@symbo-ls/workspace-shared'

// ── Eligibility ─────────────────────────────────────────────────────────────

// Pure: `root` + the live `facts` the caller gathered (onboardingFacts) →
// { ok, reason }. A reason that is not `ok` says WHY, so the gate knows
// whether to wait (an overlay will close) or to stand down (decided, not new).
// `ok` has three reasons: 'new-account' (the full welcome, on the home),
// 'fly-ready' (Fly's pending welcome — the one ready screen, on the home or
// on the app builder) and 'pob-build' (a personalized sign-up's pending
// welcome — the build board, on the home). The ready screen waits ('setup') while the workspace
// still builds itself in front of the person (functions/workspaceSetup.js
// workspaceSetupHoldsReady — the setup's steps, then its first build): the
// gate follows `_workspaceSetup` and looks again on its slow timer.
//   facts: { now, onHome, onBuilder, overlayOpen, immersive, deviceDecided,
//            setupHolds }
export function onboardingEligible(root, facts) {
  const NEW_ACCOUNT_MS = 14 * 24 * 60 * 60 * 1000
  const f = facts && typeof facts === 'object' ? facts : {}
  const r = root && typeof root === 'object' ? root : null
  if (!r || !r._authReady || !r.signedIn) return { ok: false, reason: 'auth' }
  if (r._onboardingDecision) return { ok: false, reason: 'decided' }
  if (f.deviceDecided) return { ok: false, reason: 'decided' }
  const prefs = r.userPrefs
  // The record is the authority: no answer from the row yet, no welcome (a
  // person who skipped on another device must never see it flash here).
  if (!prefs || typeof prefs !== 'object') return { ok: false, reason: 'prefs' }
  const record = prefs.onboarding && typeof prefs.onboarding === 'object' ? prefs.onboarding : null
  // THE PENDING WELCOMES — the only records that are not a decision: Fly
  // answered the questions and owes the person the ready screen; a
  // personalized sign-up (POB) owes the build board. The record is the proof,
  // so the new-account window below does not apply (an older account that ran
  // Make it real on its workspace is owed it too).
  const pending = !!(record && record.status === 'pending')
  const owedFly = pending && record.via === 'fly'
  const owedPob = pending && record.via === 'pob'
  const owed = owedFly || owedPob
  if (record && !owed) return { ok: false, reason: 'decided' }
  // A Fly arrival whose record holds no welcome: Fly asked this person every
  // question already, so the full wizard stays closed for the load.
  if (!record && r._onboardingFlyArrival) return { ok: false, reason: 'fly' }
  if (!owed) {
    const user = r.user && typeof r.user === 'object' ? r.user : null
    const created = user && user.createdAt ? Date.parse(user.createdAt) : NaN
    if (!Number.isFinite(created)) return { ok: false, reason: 'account' }
    const now = typeof f.now === 'number' ? f.now : Date.now()
    if (now - created > NEW_ACCOUNT_MS) return { ok: false, reason: 'account' }
  }
  const ws = r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  if (!ws || !(ws.id || ws._id) || ws._shape !== 'full') return { ok: false, reason: 'workspace' }
  if (r._wsSwitching || r._wpSwitching || r._dataLoading) return { ok: false, reason: 'switching' }
  // The invitee's username nudge comes first (functions/usernamePrompt.js);
  // org creation never meets a workspace here (NoOrgOverlay).
  if (r._inviteFlowActive || r._usernamePromptPending) return { ok: false, reason: 'gate' }
  // An immersive module owns this person's chrome (bellforge class): its
  // surface is the module's, and the shell's tour stops do not exist there.
  if (f.immersive) return { ok: false, reason: 'immersive' }
  // The full welcome acts on the home (its look and home steps); the ready
  // screen asks nothing of the page, so it also opens where Fly's idea
  // hand-over lands: the app builder, over the build thread. The build board
  // is the workspace's first screen: the home.
  if (!f.onHome && !(owedFly && f.onBuilder)) return { ok: false, reason: 'route' }
  if (r._homeCustomizeMode || r._canvasSlot || r._canvasSlotSecond) {
    return { ok: false, reason: 'busy' }
  }
  if (f.overlayOpen) return { ok: false, reason: 'overlay' }
  if (owedFly && f.setupHolds) return { ok: false, reason: 'setup' }
  return { ok: true, reason: owedPob ? 'pob-build' : owedFly ? 'fly-ready' : 'new-account' }
}

// The live facts eligibility reads (`this` is any shell element).
export function onboardingFacts(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const call = (name, ...args) => {
    try {
      const fns = el && el.context && el.context.functions
      return fns && typeof fns[name] === 'function' ? el.call(name, ...args) : undefined
    } catch (_) {
      return undefined
    }
  }
  const path = String((r && r.currentPath) || '')
  const stripped = call('stripWorkspacePrefix', path)
  const rest = typeof stripped === 'string' ? stripped : path
  // The assistant's MAIN layer is page content to the welcome (it opens over
  // the home for anyone who asked something); only a real layer above the
  // page — a modal, a dropdown, the open search — makes the welcome wait.
  const layer = call('shellOverlayOpen')
  const userId = String((r && r.currentUserId) || '')
  let deviceDecided = false
  try {
    const store = typeof localStorage !== 'undefined' ? localStorage : null
    deviceDecided = !!(store && userId && store.getItem('symbols_onboarding:' + userId))
  } catch (_) {
    deviceDecided = false
  }
  return {
    now: Date.now(),
    onHome: rest === '/' || rest === '',
    // the app builder — where Fly's idea hand-over lands (/add-app?prompt=…&fly=).
    // Read from the stripped route AND from the raw path (inboundPromptForBuilder
    // reads the `/w/<org>[/<ws>]` prefix itself): the strip needs the workspace
    // handle resolver to know segment 2, and an unknown handle would leave
    // `/<handle>/add-app` — a builder the screen then never meets.
    onBuilder:
      rest === '/add-app' || rest === '/add-app/' || call('inboundPromptForBuilder', path) === true,
    overlayOpen: layer === 'modalRoot' || layer === 'dropdown' || layer === 'search',
    immersive: !!call('moduleImmersiveActive', r),
    deviceDecided,
    // the workspace still builds itself (WS1): the ready screen waits for it
    setupHolds: !!call('workspaceSetupHoldsReady', r)
  }
}

// THE HOLD — the first load of a NEW account shows no busy home (Nika
// 2026-10-18: "avoid showing complex ui at first load"). The record that says
// whether the person already decided (`userPrefs`) loads AFTER the boot loader
// leaves (functions/loadPrivateData.js — a non-blocking read), so between the
// two the full home would show and then the wizard would cover it. While an
// account looks new — created within the window, no decision in this session
// or on this device — and the record is not read yet, the wizard's own calm
// surface covers the home (`root._onboardingHold`); the record then either
// opens the wizard on that same surface or releases it. Pure.
//   facts: { now, onHome, immersive, deviceDecided }
export function onboardingHoldNeeded(root, facts) {
  const NEW_ACCOUNT_MS = 14 * 24 * 60 * 60 * 1000
  const f = facts && typeof facts === 'object' ? facts : {}
  const r = root && typeof root === 'object' ? root : null
  if (!r || !r._authReady || !r.signedIn) return false
  if (r._onboardingDecision || f.deviceDecided || r._onboardingFlow) return false
  // A Fly arrival goes straight on: no calm "loading your preferences" surface
  // for a person Fly just handed over (the record, once read, opens the ready
  // screen or nothing — never the full wizard).
  if (r._onboardingFlyArrival) return false
  if (r.userPrefs && typeof r.userPrefs === 'object') return false
  const user = r.user && typeof r.user === 'object' ? r.user : null
  const created = user && user.createdAt ? Date.parse(user.createdAt) : NaN
  if (!Number.isFinite(created)) return false
  const now = typeof f.now === 'number' ? f.now : Date.now()
  if (now - created > NEW_ACCOUNT_MS) return false
  if (r._wsSwitching || r._wpSwitching) return false
  if (r._inviteFlowActive || r._usernamePromptPending) return false
  if (f.immersive || !f.onHome) return false
  return true
}

// THE GATE's tick (components/OnboardingWizard.js OnboardingGate — onCreate
// and every subscribed root change). `this` is the gate. Holds the calm
// surface while a new account's record is read (onboardingHoldNeeded, never
// longer than HOLD_MS), then opens the wizard AT ONCE when the account is
// eligible — the surface is already up, so nothing waits for the home to
// settle. While only a closing layer stands in the way it looks again on a
// slow timer (a modal's close changes no root key). A Fly arrival raises no
// hold (it is not a first load that would show a busy home: the person comes
// from Fly to a build or to the Fly screen), and Fly's pending welcome opens
// the ready screen instead of the wizard (functions/onboardingReady.js).
export function onboardingTick() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const HOLD_MS = 6000
  const WAIT = ['overlay', 'busy', 'switching']
  // looked at again on the slow timer: the waits above, and the setup's hold
  // (its windows and its first build's state move without a key the gate
  // follows) — the setup never keeps the calm surface up over its own thread
  const LOOK_AGAIN = WAIT.concat(['setup'])
  const facts = el.call('onboardingFacts', root)
  const release = () => {
    if (el.__onboardingHoldTimer) {
      clearTimeout(el.__onboardingHoldTimer)
      el.__onboardingHoldTimer = null
    }
    if (root._onboardingHold) root.update({ _onboardingHold: false }, { preventFetch: true })
  }
  if (el.__onboardingShown) {
    if (root._onboardingHold) release()
    return false
  }
  const verdict = el.call('onboardingEligible', root, facts) || {}
  if (verdict.ok) {
    el.__onboardingShown = true
    if (el.__onboardingTimer) {
      clearTimeout(el.__onboardingTimer)
      el.__onboardingTimer = null
    }
    if (el.__onboardingHoldTimer) {
      clearTimeout(el.__onboardingHoldTimer)
      el.__onboardingHoldTimer = null
    }
    // Fly's pending welcome: the ready screen (it reads its one slow fact,
    // freezes what it shows, then opens — functions/onboardingReady.js). A
    // personalized sign-up's: the build board (it reads the board first —
    // functions/onboardingBuild.js). The sign-up race: on the home, a pending
    // Fly record asks the board first (a 200 is a personalized sign-up) when
    // this SDK can read it — the feature gate is the method itself.
    if (verdict.reason === 'fly-ready') {
      const fns = (el.context && el.context.functions) || {}
      let sdk = null
      if (typeof fns.getSDK === 'function') {
        try {
          sdk = el.call('getSDK')
        } catch (_) {
          sdk = null
        }
      }
      const canRead = !!(
        sdk &&
        sdk.isNullClient !== true &&
        typeof sdk.getOnboardingBuild === 'function'
      )
      if (facts && facts.onHome && canRead) el.call('onboardingBuildOpen', { orElse: 'ready' })
      else el.call('onboardingReadyOpen')
    } else if (verdict.reason === 'pob-build') el.call('onboardingBuildOpen')
    else el.call('openOnboarding', 'welcome')
    return true
  }
  // Still reading the record of a new account: hold the calm surface, once,
  // for at most HOLD_MS — a record that never answers must not trap the home.
  if (!el.__onboardingHoldSpent && el.call('onboardingHoldNeeded', root, facts)) {
    if (!root._onboardingHold) {
      root.update({ _onboardingHold: true }, { preventFetch: true })
      el.__onboardingHoldTimer = setTimeout(() => {
        el.__onboardingHoldTimer = null
        el.__onboardingHoldSpent = true
        const r = el.state && el.state.root
        if (r && r._onboardingHold && !r._onboardingFlow) {
          r.update({ _onboardingHold: false }, { preventFetch: true })
        }
      }, HOLD_MS)
    }
  } else if (root._onboardingHold && !root._onboardingFlow && WAIT.indexOf(verdict.reason) === -1) {
    // Decided, not new, not the home: the home shows.
    release()
  }
  if (LOOK_AGAIN.indexOf(verdict.reason) !== -1 && !el.__onboardingTimer) {
    el.__onboardingTimer = setTimeout(() => {
      el.__onboardingTimer = null
      el.call('onboardingTick')
    }, 2500)
  }
  return false
}

export function onboardingGateStop() {
  const el = this
  if (el && el.__onboardingTimer) {
    clearTimeout(el.__onboardingTimer)
    el.__onboardingTimer = null
  }
  if (el && el.__onboardingHoldTimer) {
    clearTimeout(el.__onboardingHoldTimer)
    el.__onboardingHoldTimer = null
  }
  const root = el && el.state && el.state.root
  if (root && root._onboardingHold && typeof root.update === 'function') {
    root.update({ _onboardingHold: false }, { preventFetch: true })
  }
}

// ── The wizard ──────────────────────────────────────────────────────────────

// Open the wizard at a step: 'welcome' (the first-run greeting), a wizard
// step ('language', 'apps', 'look', 'home', 'done'), 'assistant' (the
// conversation), 'ready' (Fly's one ready screen — `opts.made`, the facts it
// froze, rides in; functions/onboardingReady.js) or 'build' (a personalized
// sign-up's build board — functions/onboardingBuild.js). `opts.replay` — opened on
// demand (the Personalise panel), so closing it writes no decision. The flow
// lands first and the open flag a frame later, so the surface fades in from
// its closed pose; the hold (if up) hands its surface over in the same update.
export function openOnboarding(step, opts) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const o = opts && typeof opts === 'object' ? opts : {}
  const STEPS = [
    'welcome',
    'work',
    'stack',
    'idea',
    'language',
    'apps',
    'look',
    'home',
    'done',
    'assistant',
    'ready',
    'build'
  ]
  const flow = {
    step: STEPS.indexOf(step) !== -1 ? step : 'welcome',
    replay: !!o.replay,
    progressed: false,
    picked: {},
    picks: {},
    turns: [],
    ask: null,
    answered: 0,
    seq: 0,
    busy: false
  }
  // The ready screen shows what Fly made, frozen at the moment it opens: a
  // row that landed later would shift a screen the person is reading.
  if (flow.step === 'ready') {
    flow.made = o.made && typeof o.made === 'object' ? o.made : { apps: [], site: null }
  }
  root.update(
    {
      _onboardingFlow: flow,
      _homeCustomizeMode: false,
      _homeCustomizeDrawerTucked: false,
      ...(root._onboardingHold ? { _onboardingOpen: true, _onboardingHold: false } : {})
    },
    { preventFetch: true }
  )
  // The three choices' reads (an owner/admin): the preset registry — which
  // also settles whether this host's SDK can apply presets — and the App
  // Store's apps (functions/onboardingPresets.js). The ready screen and the
  // build board ask none of the three: Fly or the sign-up answered them.
  if (flow.step !== 'ready' && flow.step !== 'build') el.call('onboardingPresetsPrime', root)
  if (flow.step === 'assistant') el.call('onboardingChatStart')
  setTimeout(() => {
    if (root._onboardingFlow && !root._onboardingOpen) {
      root.update({ _onboardingOpen: true }, { preventFetch: true })
    }
  }, 30)
  el.call('onboardingWizardFocus')
  return true
}

// What closing means for the decision: after an answer in the conversation it
// is done (the assistant); after a pick or a step left forward it is done (the
// wizard); before either it is a skip. A replay never decides. Fly's ready
// screen is done, via 'fly', once it really showed (`opened` — the surface had
// its open flag; closeOnboarding passes it): a flow that never reached the
// screen writes nothing, so Fly's pending record still owes it. The build
// board is done, via 'pob', only when it showed AND it was `settled` — every
// step had an outcome, or its poll window passed (closeOnboarding reads
// functions/onboardingBuild.js onboardingBuildView): an earlier close decides
// nothing, and the board comes back on the next load.
export function onboardingCloseDecision(flow, opened, settled) {
  const f = flow && typeof flow === 'object' ? flow : null
  if (!f || f.replay) return null
  if (f.step === 'ready') return opened === false ? null : { status: 'done', via: 'fly' }
  if (f.step === 'build') {
    return opened !== false && settled === true ? { status: 'done', via: 'pob' } : null
  }
  if (f.step === 'assistant' && f.answered > 0) return { status: 'done', via: 'assistant' }
  if (f.progressed || f.answered > 0) return { status: 'done', via: 'wizard' }
  return { status: 'skipped', via: 'skip' }
}

// The wizard's ONE close path — Escape, "Skip setup", and the end of every
// way forward. `reason` names what closed it: a bare close or a skip decides
// (onboardingCloseDecision); 'finish' and 'tour' were decided by the caller.
export function closeOnboarding(reason) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const flow =
    root._onboardingFlow && typeof root._onboardingFlow === 'object' ? root._onboardingFlow : null
  const why = typeof reason === 'string' ? reason : 'close'
  const wasOpen = !!root._onboardingOpen
  if (flow && (why === 'close' || why === 'skip')) {
    // the build board decides only once it settled (its own view says so)
    const view = flow.step === 'build' ? el.call('onboardingBuildView', root) || {} : null
    const settled = !!(view && view.ctaShown)
    const d = el.call('onboardingCloseDecision', flow, !!root._onboardingOpen, settled)
    if (d) el.call('onboardingDecide', d.status, d.via)
  }
  // The held app picks are the person's: a close keeps them, like every pick
  // the wizard already applied.
  if (flow && flow.step === 'apps') el.call('onboardingWizardApplyApps')
  // "Skip" never loses an earlier answer: a work pick that was not sent yet
  // goes now, with Your apps as they stand (once per flow — a no-op after the
  // send); a finished apply's lines leave with the welcome
  // (functions/onboardingPresets.js).
  if (flow) {
    el.call('onboardingStackSend')
    el.call('onboardingSetupRelease')
    // …and a typed workspace name is the person's too (round 2 BLOCKER #5):
    // it lands on the first workspace whatever closed the welcome.
    el.call('onboardingNameSave')
  }
  root.update({ _onboardingOpen: null, _onboardingHold: false }, { preventFetch: true })
  if (wasOpen && (why === 'close' || why === 'skip')) {
    const idea = String((flow && flow.picks && flow.picks.idea) || '').trim()
    const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
    setTimeout(() => host.call('onboardingExitToComposer', idea), 320)
  }
  return true
}

// "Skip setup" — the top bar's skip and the conversation's.
export function onboardingSkip() {
  return this.call('closeOnboarding', 'skip')
}

// The surface's document keydown (OnboardingWizard `onDocumentKeydown`): a
// key that no element holds. The boot screen (index.html lockBodyInput) keeps
// every body child `inert` for about three seconds after load, every focus()
// in that time is a no-op, and the welcome can open inside that window — its
// Escape and Tab then land on the page body and reach nobody. Once the lock is
// off, such a key is the surface's: Escape closes it, Tab enters its cycle. A
// key on an element stays that element's (the surface's own onKeydown holds
// its Escape and its Tab cycle), and nothing runs while the boot lock still
// covers the surface.
export function onboardingDocumentKeydown(e) {
  const el = this
  const node = el && el.node
  const root = el && el.state && el.state.root
  if (!e || e.defaultPrevented || !node || !node.isConnected || !root) return false
  if (!root._onboardingOpen || !root._onboardingFlow) return false
  const doc = node.ownerDocument
  const target = e.target
  if (target && doc && target !== doc && target !== doc.body && target !== doc.documentElement) {
    return false
  }
  if (typeof node.closest === 'function' && node.closest('[inert]')) return false
  if (e.key === 'Escape') {
    if (typeof e.preventDefault === 'function') e.preventDefault()
    el.call('closeOnboarding', 'close')
    return true
  }
  if (e.key === 'Tab') {
    el.call('appDialogTrapTab', e)
    return true
  }
  return false
}

// The welcome's ways forward: 'wizard' (Get started), 'assistant' (the
// conversation), 'tour'.
export function onboardingChoose(path) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const flow =
    root._onboardingFlow && typeof root._onboardingFlow === 'object' ? root._onboardingFlow : {}
  // The welcome's button leaves with the welcome: give its focus up now, so
  // the next step's heading can take it — a focus left on a hidden button lost
  // Escape and the Tab cycle (live on dev 76305a52b at a phone width).
  try {
    if (el.node && typeof el.node.blur === 'function') el.node.blur()
  } catch (_) {
    /* focus is a courtesy — never an error */
  }
  if (path === 'wizard') {
    if (flow.step !== 'welcome') {
      root.update({ _onboardingFlow: { ...flow, step: 'welcome' } }, { preventFetch: true })
    }
    return !!el.call('onboardingWizardGo', 1)
  }
  if (path === 'assistant') {
    root.update({ _onboardingFlow: { ...flow, step: 'assistant' } }, { preventFetch: true })
    el.call('onboardingChatStart')
    return true
  }
  if (path === 'tour') {
    if (!flow.replay) el.call('onboardingDecide', 'done', 'tour')
    el.call('closeOnboarding', 'tour')
    // After the surface's exit, so the first stop is measured on a still page.
    // Through the app root: the button that asked may be gone by then.
    const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
    setTimeout(() => host.call('onboardingTourStart'), 320)
    return true
  }
  return false
}

// The Personalise panel's "Getting started" group: replay the wizard, the
// assistant or the tour on demand. Edit mode closes first — the drawer covers
// the chrome. A replayed wizard starts at its first choice (the greeting is
// for the first run).
export function onboardingReplay(path) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  root.update(
    { _homeCustomizeMode: false, _homeCustomizeDrawerTucked: false },
    { preventFetch: true }
  )
  if (path === 'tour') {
    const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
    setTimeout(() => host.call('onboardingTourStart'), 320)
    return true
  }
  if (path === 'wizard') {
    el.call('openOnboarding', 'welcome', { replay: true })
    return !!el.call('onboardingWizardGo', 1)
  }
  // The avatar entry offers both setup and the tour without changing the
  // person's existing completion record or any preference just by opening it.
  if (path === 'welcome') return el.call('openOnboarding', 'welcome', { replay: true })
  return el.call('openOnboarding', 'assistant', { replay: true })
}

// ── The decision ────────────────────────────────────────────────────────────

// `el.call('onboardingDecide', 'done', 'tour')`. Writes this session's copy,
// this device's copy, the mirrored row when it has loaded, then the record on
// the shared single-flight queue. The first decision of a session wins
// (`opts.force` overrides). Returns the write's promise (tests await it).
// `opts`: { root, sdk, storage, now, force } — test seams.
export function onboardingDecide(status, via, opts) {
  const STATUSES = ['done', 'skipped']
  const VIAS = ['wizard', 'assistant', 'tour', 'skip', 'ask', 'fly', 'pob']
  const el = this && typeof this === 'object' ? this : null
  const o = opts && typeof opts === 'object' ? opts : {}
  const root = o.root || (el && el.state && el.state.root) || null
  const sdk = o.sdk || (el && el.sdk) || null
  if (!root || typeof root.update !== 'function') return Promise.resolve(null)
  if (root._onboardingDecision && !o.force) return Promise.resolve(null)
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  const bag = {
    v: 1,
    status: STATUSES.indexOf(status) !== -1 ? status : 'skipped',
    via: VIAS.indexOf(via) !== -1 ? via : 'skip',
    at: new Date(typeof o.now === 'number' ? o.now : Date.now()).toISOString()
  }
  // A Fly sign-up keeps where it came from through its decision: Fly's record
  // (`via: 'fly'`, pending or done) becomes `done` via the TOUR and would stop
  // saying so — and the two readers of that word (components/NoOrgOverlay.js:
  // the org question Fly answered must stay answered, or a non-dismissable
  // "create a workspace" modal opens over the tour; and the Fly start screen's
  // "Fly set up <workspace> with you") would lose the person. `origin: 'fly'`
  // carries it. Fly's checklist reads `status` only. A personalized sign-up
  // keeps `origin: 'pob'` the same way: the first thread's research summary
  // and its offers follow that word (functions/assistantFlyResearch.js).
  const before =
    prefs && prefs.onboarding && typeof prefs.onboarding === 'object' ? prefs.onboarding : null
  if (before && (before.via === 'fly' || before.origin === 'fly') && bag.via !== 'fly') {
    bag.origin = 'fly'
  } else if (before && (before.via === 'pob' || before.origin === 'pob') && bag.via !== 'pob') {
    bag.origin = 'pob'
  }
  const patch = { _onboardingDecision: bag }
  if (prefs) patch.userPrefs = { ...prefs, onboarding: bag }
  root.update(patch, { preventFetch: true })
  try {
    const store = o.storage || (typeof localStorage !== 'undefined' ? localStorage : null)
    const userId = String(root.currentUserId || '')
    if (store && userId) store.setItem('symbols_onboarding:' + userId, JSON.stringify(bag))
  } catch (err) {
    console.warn('[onboarding] device copy write failed:', err && err.message)
  }
  if (!sdk || typeof sdk.execute !== 'function') return Promise.resolve(null)
  // The queue every user_preferences writer shares, keyed as they key it
  // (functions/homeLook.js homeLookSet).
  const w = root.workspace
  const key =
    String(
      (w && (w.id || w._id)) || (root.jwtClaims && root.jwtClaims.active_workspace_id) || ''
    ) || 'user'
  if (!root.__navbarSaveQueue) root.__navbarSaveQueue = new Map()
  const queue = root.__navbarSaveQueue
  const prior = queue.get(key) || Promise.resolve()
  const task = prior
    .catch(() => {})
    .then(async () => {
      // PER-PATH PATCH — names only `onboarding` (CORE-USERPREFS-PATCH-PER-PATH-1).
      const row = await sdk.execute('workspaceProject.userPreferences', 'patch', {
        patch: { set: { onboarding: bag } }
      })
      if (row && typeof row === 'object') {
        const now = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
        root.update(
          { userPrefs: now ? { ...now, onboarding: row.onboarding || bag } : row },
          { preventFetch: true }
        )
      }
      return row
    })
  queue.set(key, task)
  return task.catch((err) => {
    console.warn('[onboarding] decision save failed:', err && err.message)
    return null
  })
}

// A FLY ARRIVAL — Fly's hand-over: `?fly` in the address (the home's
// `/?fly=<ask>`, the app builder's `/add-app?prompt=<idea>&fly=`, fly docs/
// SYMBOLS.md §5). It marks THIS PAGE LOAD (`root._onboardingFlyArrival`) and
// writes no decision: the mark keeps the new-account hold down and the full
// wizard closed (onboardingHoldNeeded, onboardingEligible), while the welcome
// itself follows Fly's own record — `pending` owes the one ready screen, any
// other record is a decision (functions/onboardingReady.js). It used to write a
// session `done` here, before the record was read, and the first decision of a
// session wins: a pending record could then never show its screen.
// `search`: a query string to look at (the shell's boot reads the address bar
// before any page strips `fly`); without one the caller has seen the mark
// already (the app builder's mount). → true when it marked.
export function onboardingNoteFlyArrival(root, search) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  if (!r || typeof r.update !== 'function' || r._onboardingFlyArrival) return false
  if (typeof search === 'string' && !new URLSearchParams(search).has('fly')) return false
  r.update({ _onboardingFlyArrival: true }, { preventFetch: true })
  return true
}

// ── Names the copy speaks ───────────────────────────────────────────────────

// The person's first name, '' when the record has none (the copy then reads
// without a name — never a placeholder word, the greeting's own rule).
export function onboardingFirstName(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const u = r && r.user && typeof r.user === 'object' ? r.user : null
  const profile = u && u.profile && typeof u.profile === 'object' ? u.profile : null
  const name = (u && u.name) || (profile && (profile.firstName || profile.name)) || ''
  return String(name).trim().split(/\s+/)[0] || ''
}

// The workspace's display name (the white-label brand's on its own host).
export function onboardingWorkspaceName(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const brand = r && r.whitelabelBrand && r.whitelabelBrand.name
  return String((ws && (ws.displayName || ws.name)) || brand || '').trim()
}

// The text of a bundle key with `{name}` / `{workspace}` / `{language}` /
// `{preset}` filled in from the live root (`params` overrides). Resolved at
// render, so a language switch re-renders every line of the conversation.
// `{preset}` is the name of the preset the flow picked, else the one the
// workspace is set up with (functions/onboardingPresets.js).
export function onboardingText(key, params) {
  const el = this
  if (!el || typeof el.call !== 'function' || !key) return ''
  const root = el.state && el.state.root
  const p = params && typeof params === 'object' ? params : {}
  const raw = String(el.call('polyglot', key) || '')
  const values = {
    name: p.name != null ? p.name : el.call('onboardingFirstName', root),
    workspace: p.workspace != null ? p.workspace : el.call('onboardingWorkspaceName', root),
    language: p.language != null ? p.language : '',
    preset: p.preset != null ? p.preset : ''
  }
  if (!values.language && raw.indexOf('{language}') !== -1) {
    const lang = el.call('onboardingLanguage', root) || {}
    const current = (lang.choices || []).find((c) => c.value === lang.current)
    values.language = current ? current.label : ''
  }
  if (!values.preset && raw.indexOf('{preset}') !== -1) {
    values.preset = String(el.call('onboardingPresetName', root) || '')
  }
  return raw.replace(/\{(name|workspace|language|preset)\}/g, (m, k) => String(values[k] || ''))
}

// ── The language ────────────────────────────────────────────────────────────

// { current, canChange, choices: [{ value, label }] } — the workspace's
// resolved language (THE LAW's chain: settings.language → the white-label
// brand's → English), whether this person may set it (the org roles the admin
// workspace-settings page gates on), and the shipped languages by endonym.
export function onboardingLanguage(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const ADMIN_ROLES = ['owner', 'co-owner', 'admin', 'maintainer']
  const shared = (_wsShared && _wsShared.functions) || {}
  const registry = Array.isArray(shared.PLATFORM_LANGUAGES) ? shared.PLATFORM_LANGUAGES : null
  const list = (
    registry || [
      { code: 'en', endonym: 'English' },
      { code: 'ka', endonym: 'ქართული' }
    ]
  ).filter((l) => l && typeof l.code === 'string')
  const ok = (c) => typeof c === 'string' && list.some((l) => l.code === c)
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const settings = ws && ws.settings && typeof ws.settings === 'object' ? ws.settings : null
  const brand = r && r.whitelabelBrand && r.whitelabelBrand.lang
  const current =
    (settings && ok(settings.language) ? settings.language : null) ||
    (ok(brand) ? brand : null) ||
    'en'
  const role = r && r.org && r.org.role
  return {
    current,
    canChange: !!(ws && (ws.id || ws._id)) && ADMIN_ROLES.indexOf(role) !== -1,
    choices: list.map((l) => ({ value: l.code, label: String(l.endonym || l.code) }))
  }
}

// Set the WORKSPACE's language (admins). The same per-key PATCH as
// /admin/workspace-settings, optimistic with a rollback; on success the held
// record converges through setActiveWorkspaceRecord, whose language listener
// (app.js → applyWorkspaceLangPolicy) repaints the session — never setLang
// directly, which is the PERSON's path and is refused inside a workspace.
export async function onboardingSetWorkspaceLanguage(code) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const lang = el.call('onboardingLanguage', root) || {}
  const supported = (lang.choices || []).some((c) => c.value === code)
  if (!lang.canChange || !supported) return false
  if (code === lang.current) return true
  const ws = root.workspace
  const wsId = ws && (ws.id || ws._id)
  const sdk = el.sdk
  const failed = (messageKey) =>
    el.call('openNotification', {
      title: el.call('polyglot', 'settings.language.saveError'),
      message: el.call('polyglot', messageKey),
      type: 'error'
    })
  if (!wsId || !sdk || typeof sdk.updateWorkspaceSettings !== 'function') {
    failed(wsId ? 'settings.language.notReady' : 'settings.language.noWorkspace')
    return false
  }
  const prevSettings = root.workspaceSettings
  root.update(
    { workspaceSettings: { ...(root.workspaceSettings || {}), language: code } },
    { preventFetch: true }
  )
  try {
    const res = await sdk.updateWorkspaceSettings(wsId, { language: code })
    if (res && res.success === false) throw new Error(res.message || '')
  } catch (err) {
    root.update({ workspaceSettings: prevSettings }, { preventFetch: true })
    console.warn('[onboarding] workspace language save failed:', err && err.message)
    failed('settings.language.adminOnly')
    return false
  }
  const held = root.workspace
  if (held && String(held.id || held._id || '') === String(wsId)) {
    el.call(
      'setActiveWorkspaceRecord',
      root,
      { ...held, settings: { ...(held.settings || {}), language: code } },
      { reason: 'onboarding:language' }
    )
  }
  return true
}

// ── The conversation ────────────────────────────────────────────────────────

// THE PLAN — the questions this person is asked, in order, with their answers.
// One source for the chat, its progress line and its tests. Labels are bundle
// keys (polyglot returns a non-key verbatim, so an endonym passes as it is).
export function onboardingPlan(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const plan = []
  // THE THREE CHOICES first, as in the wizard (wt/presets CONTRACT §1): Your
  // work — the featured presets and Something else, asked only while the
  // workspace has no preset and this flow sent none (never ask twice; the
  // conversation's own answer keeps its place, so the count stands) — then
  // Your idea, where a TYPED answer is the idea and each example chip answers
  // with its own sentence (functions/onboardingPresets.js).
  const flow = r && r._onboardingFlow
  const sent = !!(flow && flow.stackSent && !flow.workViaChat)
  if (el.call('onboardingPresetMode', r) === 'pick' && !sent) {
    // the conversation offers the tiles' presets, never the grid's more tile
    const tiles = (el.call('onboardingWorkTiles', r) || []).filter((t) => t && !t.more)
    if (tiles.length > 1) {
      plan.push({
        id: 'work',
        ask: 'workspace.onboarding.chat.askWork',
        skip: 'workspace.onboarding.chat.keepThis',
        kind: 'option',
        choices: tiles.map((t) => ({ value: t.value, label: t.label, hint: t.hint }))
      })
    }
  }
  if (el.call('onboardingIdeaAsked', r)) {
    plan.push({
      id: 'idea',
      ask: 'workspace.onboarding.chat.askIdea',
      skip: 'workspace.onboarding.chat.skipQuestion',
      typed: true,
      choices: (el.call('onboardingIdeaExamples') || []).map((x) => ({
        value: x.label,
        label: x.label
      }))
    })
  }
  plan.push({
    id: 'theme',
    ask: 'workspace.onboarding.chat.askTheme',
    skip: 'workspace.onboarding.chat.keepThis',
    choices: [
      { value: 'light', label: 'workspace.personalize.themeLight', icon: 'sun' },
      { value: 'dark', label: 'workspace.personalize.themeDark', icon: 'moon' },
      { value: 'auto', label: 'workspace.personalize.themeAuto', icon: 'displayPreferences' }
    ]
  })
  const skies = el.call('homeSkyChoices')
  if (Array.isArray(skies) && skies.some((s) => s && s.value !== 'none')) {
    plan.push({
      id: 'sky',
      ask: 'workspace.onboarding.chat.askSky',
      skip: 'workspace.onboarding.chat.keepThis',
      kind: 'sky',
      choices: skies.map((s) => ({ key: s.key, value: s.value, label: s.label }))
    })
  }
  plan.push({
    id: 'summary',
    ask: 'workspace.onboarding.chat.askSummary',
    skip: 'workspace.onboarding.chat.keepThis',
    kind: 'option',
    choices: [
      {
        value: 'brief',
        label: 'workspace.personalize.summaryBrief',
        hint: 'workspace.onboarding.chat.summaryBriefHint'
      },
      {
        value: 'signals',
        label: 'workspace.personalize.summarySignals',
        hint: 'workspace.onboarding.chat.summarySignalsHint'
      }
    ]
  })
  plan.push({
    id: 'strip',
    ask: 'workspace.onboarding.chat.askStrip',
    skip: 'workspace.onboarding.chat.keepThis',
    kind: 'option',
    choices: [
      {
        value: 'dock',
        label: 'workspace.onboarding.chat.stripDock',
        hint: 'workspace.onboarding.chat.stripDockHint'
      },
      {
        value: 'rail',
        label: 'workspace.onboarding.chat.stripRail',
        hint: 'workspace.onboarding.chat.stripRailHint'
      },
      {
        value: 'off',
        label: 'workspace.onboarding.chat.stripOff',
        hint: 'workspace.onboarding.chat.stripOffHint'
      }
    ]
  })
  plan.push({
    id: 'tour',
    ask: 'workspace.onboarding.chat.askTour',
    choices: [
      { value: 'tour', label: 'workspace.onboarding.chat.tourYes', icon: 'playOval' },
      { value: 'later', label: 'workspace.onboarding.chat.tourNo' }
    ]
  })
  return plan
}

// The question being asked now (null between questions and at the end).
export function onboardingQuestion(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  if (!flow || !flow.ask) return null
  const plan = el.call('onboardingPlan', r) || []
  return plan.find((q) => q.id === flow.ask) || null
}

// "2 of 5" — the place of the question being asked in the plan, or '' when
// nothing is being asked.
export function onboardingProgress(root) {
  const el = this
  const r = root || (el && el.state && el.state.root) || null
  const flow = r && r._onboardingFlow
  if (!flow || flow.step !== 'assistant' || !flow.ask) return ''
  const plan = el.call('onboardingPlan', r) || []
  const at = plan.findIndex((q) => q.id === flow.ask)
  if (at < 0) return ''
  return String(el.call('polyglot', 'workspace.onboarding.chat.progress') || '')
    .replace('{n}', String(at + 1))
    .replace('{total}', String(plan.length))
}

// Start the conversation: the greeting, the member's language line (THE LAW),
// then the first question.
export function onboardingChatStart() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const flow =
    root._onboardingFlow && typeof root._onboardingFlow === 'object' ? root._onboardingFlow : {}
  const plan = el.call('onboardingPlan', root) || []
  // The greeting reads WITHOUT a name when the record has none — never a
  // placeholder word (the home greeting's own rule).
  const hello = el.call('onboardingFirstName', root)
    ? 'workspace.onboarding.chat.hello'
    : 'workspace.onboarding.chat.helloNoName'
  const turns = [{ id: 't1', from: 'assistant', line: hello }]
  turns.push({ id: 't2', from: 'assistant', line: 'workspace.onboarding.chat.languageSet' })
  // A workspace set up already — at sign-up, or by this flow's own Your work
  // — is told so, never asked again (functions/onboardingPresets.js).
  const work = el.call('onboardingWorkView', root) || {}
  if (work.state === 'applied' || work.state === 'sent') {
    const named = !!el.call('onboardingPresetName', root)
    const custom =
      work.state === 'applied'
        ? ((el.call('workspacePresetApplied', root) || {}).key || '') === 'custom'
        : ((flow.picks && flow.picks.work) || '') === 'custom'
    turns.push({
      id: 't' + (turns.length + 1),
      from: 'assistant',
      line: custom
        ? 'workspace.onboarding.chat.workSetCustom'
        : named
          ? 'workspace.onboarding.chat.workSet'
          : 'workspace.onboarding.chat.workSetOther'
    })
  }
  const first = plan[0]
  if (first) turns.push({ id: 't' + (turns.length + 1), from: 'assistant', line: first.ask })
  root.update(
    {
      _onboardingFlow: {
        ...flow,
        step: 'assistant',
        turns,
        ask: first ? first.id : null,
        answered: 0,
        seq: turns.length,
        busy: false
      }
    },
    { preventFetch: true }
  )
  return true
}

// Apply one answer through the writers the Personalise panel uses. Returns a
// promise of true for a supported personal choice; shared settings are refused.
export async function onboardingApply(id, value) {
  const el = this
  if (!el || typeof el.call !== 'function') return false
  if (id === 'theme') {
    el.call('homeLookSetTheme', value)
    return true
  }
  if (id === 'sky') {
    el.call('homeLookSet', { sky: value })
    return true
  }
  if (id === 'summary') {
    el.call('homeLookSet', { summaryStyle: value })
    return true
  }
  if (id === 'strip') {
    if (value === 'off') el.call('homeLookSet', { stripShown: false })
    else el.call('homeLookSet', { stripShown: true, stripStyle: value })
    return true
  }
  // Your work: the pick, sent in the background with the preset's own apps;
  // Your idea: held for the finish — an example chip's sentence in the
  // session language, or the typed one as it is (functions/onboardingPresets.js).
  if (id === 'work') return !!el.call('onboardingWorkAnswer', value)
  if (id === 'idea') {
    const text = String(el.call('polyglot', value) || value || '')
    return !!el.call('onboardingIdeaSet', text)
  }
  return id === 'tour'
}

// The person answered the question being asked (a choice's `value`), or
// skipped it (`opts.skip`), or — for a question that takes one (Your idea) —
// typed it (`opts.text`). Writes the answer turn, applies it, then asks the
// next question — or ends the conversation.
export async function onboardingChatAnswer(value, opts) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof root.update !== 'function') return false
  const o = opts && typeof opts === 'object' ? opts : {}
  const flow =
    root._onboardingFlow && typeof root._onboardingFlow === 'object' ? root._onboardingFlow : null
  if (!flow || flow.step !== 'assistant' || !flow.ask || flow.busy) return false
  const plan = el.call('onboardingPlan', root) || []
  const at = plan.findIndex((q) => q.id === flow.ask)
  const q = at >= 0 ? plan[at] : null
  if (!q) return false
  const typed = !o.skip && q.typed && typeof o.text === 'string' ? o.text.trim() : ''
  const choice = o.skip
    ? null
    : typed
      ? { value: typed, label: typed }
      : (q.choices || []).find((c) => c.value === value) || null
  if (!o.skip && !choice) return false

  let seq = flow.seq || flow.turns.length
  const turns = flow.turns.slice()
  seq += 1
  turns.push({
    id: 't' + seq,
    from: 'me',
    line: o.skip ? q.skip || 'workspace.onboarding.chat.skipQuestion' : choice.label,
    // an endonym answer names its own language for assistive technology
    lang: !o.skip && choice.lang ? choice.lang : null
  })
  root.update({ _onboardingFlow: { ...flow, turns, seq, busy: true } }, { preventFetch: true })

  let landed = true
  if (choice) landed = await el.call('onboardingApply', q.id, choice.value)
  const now =
    root._onboardingFlow && typeof root._onboardingFlow === 'object' ? root._onboardingFlow : flow
  // Closed or restarted while the answer was applied — nothing to continue.
  if (!now || now.step !== 'assistant' || now.ask !== q.id) return landed
  const answered = (now.answered || 0) + (choice && landed ? 1 : 0)
  const after = now.turns.slice()
  let s2 = now.seq || after.length
  if (choice && !landed) {
    s2 += 1
    after.push({ id: 't' + s2, from: 'assistant', line: 'workspace.onboarding.chat.notSaved' })
  }
  if (!flow.replay && answered > 0) el.call('onboardingDecide', 'done', 'assistant')

  // The tour question ends the conversation: yes starts the tour, no closes
  // with the parting line.
  if (q.id === 'tour') {
    if (choice && choice.value === 'tour') {
      root.update(
        { _onboardingFlow: { ...now, turns: after, seq: s2, ask: null, answered, busy: false } },
        { preventFetch: true }
      )
      el.call('closeOnboarding', 'tour')
      // Through the app root: this answer's button leaves with the question.
      const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
      setTimeout(() => host.call('onboardingTourStart'), 320)
      return true
    }
    s2 += 1
    after.push({ id: 't' + s2, from: 'assistant', line: 'workspace.onboarding.chat.bye' })
    root.update(
      { _onboardingFlow: { ...now, turns: after, seq: s2, ask: null, answered, busy: false } },
      { preventFetch: true }
    )
    return true
  }

  // The idea is built at the finish — the conversation says so once.
  if (q.id === 'idea' && choice && landed) {
    s2 += 1
    after.push({ id: 't' + s2, from: 'assistant', line: 'workspace.onboarding.chat.ideaNext' })
  }
  const next = plan[at + 1] || null
  if (next) {
    s2 += 1
    after.push({ id: 't' + s2, from: 'assistant', line: next.ask })
  }
  root.update(
    {
      _onboardingFlow: {
        ...now,
        turns: after,
        seq: s2,
        ask: next ? next.id : null,
        answered,
        busy: false
      }
    },
    { preventFetch: true }
  )
  return landed
}

// Skip the question being asked.
export function onboardingChatSkip() {
  return this.call('onboardingChatAnswer', null, { skip: true })
}

// The ask field's sentence: the answer to a question that takes a typed one
// (Your idea), else a question for the real assistant (onboardingChatHandoff).
export function onboardingChatTyped(text) {
  const el = this
  const root = el && el.state && el.state.root
  const ask = String(text || '').trim()
  if (!root || !ask) return false
  const q = el.call('onboardingQuestion', root)
  if (q && q.typed) return el.call('onboardingChatAnswer', null, { text: ask })
  return el.call('onboardingChatHandoff', ask)
}

// Keyboard continuity: when an answer's button leaves with its question, the
// focus would fall to the page behind the veil. The NEXT question's first
// answer takes it — and only then: never while the person types in the ask
// field, never over the panel's own first focus. `answer` is the answer
// element (its onRender).
export function onboardingFocusAnswer(answer) {
  const el = answer || this
  const node = el && el.node
  if (!node || typeof node.focus !== 'function') return false
  const root = el.state && el.state.root
  if (!root || !root._onboardingOpen) return false
  // Decided after the answers are in the document: at render a row is not
  // attached yet, so EVERY row looked first and the last one won the focus
  // (live on dev 76305a52b: the sky question focused "None").
  setTimeout(() => {
    try {
      if (!node.isConnected || node.previousElementSibling) return
      const doc = node.ownerDocument
      const active = doc && doc.activeElement
      if (active && active !== doc.body && active.isConnected) return
      node.focus({ preventScroll: true })
    } catch (_) {
      /* focus is a courtesy — never an error */
    }
  }, 0)
  return true
}

// A typed question goes to the real assistant — the composer's own path, in
// the Assistant mode for this one ask (an answer, never a board or a page).
export function onboardingChatHandoff(text) {
  const el = this
  const root = el && el.state && el.state.root
  const ask = String(text || '').trim()
  if (!root || !ask) return false
  const flow = root._onboardingFlow
  if (flow && !flow.replay) el.call('onboardingDecide', 'done', 'ask')
  el.call('closeOnboarding', 'ask')
  const host = (typeof el.getRoot === 'function' && el.getRoot()) || el
  setTimeout(() => {
    try {
      host.call('homeAiSend', ask, null, 0, null, { mode: 'assistant' })
    } catch (err) {
      console.warn('[onboarding] hand-off failed:', err && err.message)
    }
  }, 120)
  return true
}
